import React, { useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../design-system/components/composite/Modal/Modal";
import { Button, Switch } from "../../atoms";
import { DateField, Dropdown, SchedulePicker } from "../../molecules";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import { isoDateTodayLocal } from "../../../utils/scheduleTypes";
import {
  useScheduleRefresh,
  type ScheduleRefreshDropdownKey,
} from "./useScheduleRefresh";
import {
  ActivationWarningBox,
  ActivationWarningIcon,
  ActivationWarningText,
  CancelButton,
  Field,
  FieldLabel,
  FooterActions,
  FooterRow,
  Form,
  HeaderTitle,
  HelperText,
  IncrementalRow,
  IncrementalSubtitle,
  IncrementalText,
  IncrementalTitle,
  SaveScheduleButton,
} from "./ScheduleRefreshModal.styles";
import type {
  ScheduleRefreshConfig,
  ScheduleRefreshErrors,
  ScheduleRefreshModalProps,
} from "./ScheduleRefreshModal.types";

const showError = (
  showErrors: boolean,
  errors: ScheduleRefreshErrors,
  key: keyof ScheduleRefreshErrors,
): boolean => showErrors && Boolean(errors[key]);

/** Narrow the modal-level dropdown enum to the subset accepted by SchedulePicker. */
const toBaseDropdown = (
  key: ScheduleRefreshDropdownKey | null,
): "unit" | "hour" | "timezone" | null =>
  key === "unit" || key === "hour" || key === "timezone" ? key : null;

const WarningTriangleIcon = () => (
  <svg fill="none" viewBox="0 0 16 16" width="16" height="16">
    <path
      d="M8.00065 5.99992V8.66659M8.00065 11.3333H8.00732M7.05999 2.57347L1.21332 12.0001C1.09718 12.2017 1.03573 12.4301 1.03498 12.6627C1.03424 12.8953 1.09423 13.1241 1.20909 13.3264C1.32394 13.5287 1.49 13.6975 1.69111 13.8155C1.89222 13.9335 2.12139 13.9966 2.35399 13.9999H14.0473C14.2799 13.9966 14.5091 13.9335 14.7102 13.8155C14.9113 13.6975 15.0774 13.5287 15.1922 13.3264C15.3071 13.1241 15.3671 12.8953 15.3663 12.6627C15.3656 12.4301 15.3041 12.2017 15.188 12.0001L9.34132 2.57347C9.22281 2.37761 9.05633 2.21721 8.85722 2.10681C8.65812 1.99641 8.43321 1.93994 8.20465 1.93994C7.9761 1.93994 7.75119 1.99641 7.55208 2.10681C7.35298 2.21721 7.1865 2.37761 7.06799 2.57347H7.05999Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Schedule Refresh modal — connector dataset refresh scheduling.
 *
 * Composed from the shared `SchedulePicker` molecule (every / repeat-on /
 * refresh-at — same UI as DeliverStory) plus the modal-specific Incremental
 * refresh + Incremental column rows. Validation + dropdown state are owned
 * by `useScheduleRefresh`.
 */
export const ScheduleRefreshModal: React.FC<ScheduleRefreshModalProps> = ({
  open,
  onClose,
  table,
  mode = "create",
  initialValue,
  incrementalColumnOptions,
  onSubmit,
  onSaveAndActivate,
}) => {
  const {
    config,
    errors,
    showErrors,
    openDropdown,
    patch,
    toggleWeekday,
    openDropdownKey,
    closeDropdown,
    attemptSubmit,
  } = useScheduleRefresh({ initialValue, open });

  // Only the modal owns the incremental-column dropdown — the rest delegate
  // to SchedulePicker.
  const incrementalColumnPortal = usePortalMenu<
    HTMLButtonElement,
    HTMLUListElement
  >({
    open: openDropdown === "incrementalColumn",
    onOutsideClick: closeDropdown,
  });

  const patchSchedule = useCallback(
    (partial: Partial<ScheduleRefreshConfig>) => {
      const next: Partial<ScheduleRefreshConfig> = { ...partial };
      if ("unit" in partial && partial.unit !== undefined) {
        if (partial.unit !== "month") {
          next.startsOn = "";
        }
      }
      patch(next);
    },
    [patch],
  );

  const handleSave = useCallback(() => {
    if (!attemptSubmit()) return;
    void onSubmit(table, config);
  }, [attemptSubmit, config, onSubmit, table]);

  const handleSaveAndActivate = useCallback(() => {
    if (!attemptSubmit() || !onSaveAndActivate) return;
    void onSaveAndActivate(table, config);
  }, [attemptSubmit, config, onSaveAndActivate, table]);

  return (
    <Modal open={open} onClose={onClose} size="md">
      <ModalHeader onClose={onClose}>
        <HeaderTitle>
          {mode === "edit" ? "Edit schedule" : "Schedule refresh"}
        </HeaderTitle>
      </ModalHeader>

      <ModalBody>
        <Form>
          <SchedulePicker
            value={config}
            errors={errors}
            showErrors={showErrors}
            openDropdown={toBaseDropdown(openDropdown)}
            onChange={patchSchedule}
            onToggleWeekday={toggleWeekday}
            onOpenDropdown={openDropdownKey}
            onCloseDropdown={closeDropdown}
          />

          {config.unit === "month" && (
            <Field>
              <FieldLabel>Starts on</FieldLabel>
              <DateField
                value={config.startsOn}
                min={isoDateTodayLocal()}
                invalid={showError(showErrors, errors, "startsOn")}
                ariaLabel="Start date"
                onChange={(iso) => patch({ startsOn: iso })}
              />
              {showError(showErrors, errors, "startsOn") && (
                <HelperText $error>{errors.startsOn}</HelperText>
              )}
            </Field>
          )}

          <IncrementalRow>
            <IncrementalText>
              <IncrementalTitle>Incremental refresh</IncrementalTitle>
              <IncrementalSubtitle>
                Only new or updated data will be refreshed to keep things fast
                and efficient.
              </IncrementalSubtitle>
            </IncrementalText>
            <Switch
              value={config.incrementalEnabled}
              onChange={(next) =>
                patch({
                  incrementalEnabled: next,
                  incrementalColumn: next ? config.incrementalColumn : null,
                })
              }
              ariaLabel="Toggle incremental refresh"
              size="sm"
            />
          </IncrementalRow>

          {config.incrementalEnabled && (
            <Field>
              <FieldLabel>Incremental column</FieldLabel>
              <Dropdown
                value={config.incrementalColumn}
                options={incrementalColumnOptions}
                open={openDropdown === "incrementalColumn"}
                triggerRef={incrementalColumnPortal.triggerRef}
                menuRef={incrementalColumnPortal.menuRef}
                menuRect={incrementalColumnPortal.rect}
                size="md"
                placeholder="Select a column"
                invalid={showError(showErrors, errors, "incrementalColumn")}
                ariaLabel="Incremental column"
                onToggle={() => openDropdownKey("incrementalColumn")}
                onSelect={(v) => {
                  patch({ incrementalColumn: v });
                  closeDropdown();
                }}
              />
              <HelperText
                $error={showError(showErrors, errors, "incrementalColumn")}
              >
                {showError(showErrors, errors, "incrementalColumn")
                  ? errors.incrementalColumn
                  : "Choose a column that updates when your data changes (e.g., last updated date)."}
              </HelperText>
            </Field>
          )}

          <ActivationWarningBox>
            <ActivationWarningIcon>
              <WarningTriangleIcon />
            </ActivationWarningIcon>
            <ActivationWarningText>
              Adding this schedule will pause the currently active schedule. You
              can switch it anytime.
            </ActivationWarningText>
          </ActivationWarningBox>
        </Form>
      </ModalBody>

      <ModalFooter>
        <FooterRow>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <FooterActions>
            <SaveScheduleButton onClick={handleSave}>
              Save schedule
            </SaveScheduleButton>
            {onSaveAndActivate && (
              <Button
                variant="primary-dark"
                size="md"
                onClick={handleSaveAndActivate}
                style={{ borderRadius: "8px" }}
              >
                Save and activate
              </Button>
            )}
          </FooterActions>
        </FooterRow>
      </ModalFooter>
    </Modal>
  );
};

export default ScheduleRefreshModal;
